// ArbitrageCalculator.jsx
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AlertCircle, Calculator } from 'lucide-react';
import { OddsConverter } from '@/lib/OddsConverter';
import { TaxCalculator } from '@/lib/TaxCalculator';

const ArbitrageCalculator = () => {
  const [sportType, setSportType] = useState('SOCCER');
  const [includeDraw, setIncludeDraw] = useState(false);
  const [taxRegion, setTaxRegion] = useState('No Tax');
  const [customTaxRate, setCustomTaxRate] = useState(20);
  const [customTaxType, setCustomTaxType] = useState('Profit');
  
  const [bookmakers, setBookmakers] = useState({
    win: { name: 'Winning Odds', format: OddsConverter.FORMATS.DECIMAL, odds: '2.0' },
    lose: { name: 'Losing Odds', format: OddsConverter.FORMATS.DECIMAL, odds: '2.0' },
    draw: { name: 'Draw Odds', format: OddsConverter.FORMATS.DECIMAL, odds: '3.0' }
  });
  
  const [investment, setInvestment] = useState(1000);
  const [results, setResults] = useState(null);

  const calculateArbitrage = () => {
    try {
      const bookmakerList = [
        { type: 'win', ...bookmakers.win },
        { type: 'lose', ...bookmakers.lose },
        ...(includeDraw ? [{ type: 'draw', ...bookmakers.draw }] : [])
      ];

      // Validate all odds formats before calculation
      const invalidOdds = bookmakerList.find(bm => 
        !OddsConverter.validateOdds(bm.odds, bm.format)
      );
      
      if (invalidOdds) {
        throw new Error(`Invalid odds format for ${invalidOdds.type}: ${invalidOdds.odds}`);
      }

      // Convert all odds to decimal format
      const decimalOdds = bookmakerList.map(bm => 
        OddsConverter.toDecimal(bm.odds, bm.format)
      );

      const probabilities = decimalOdds.map(odds => 1/odds);
      const totalProb = probabilities.reduce((a, b) => a + b, 0);
      
      if (totalProb >= 1) {
        setResults({
          exists: false,
          totalProbability: totalProb * 100
        });
        return;
      }
      
      const stakes = probabilities.map(prob => investment * prob / totalProb);
      const returns = stakes.map((stake, i) => stake * decimalOdds[i]);
      
      // Calculate tax
      const minReturn = Math.min(...returns);
      const profit = minReturn - investment;
      const taxResult = TaxCalculator.calculateTax(
        profit,
        investment,
        taxRegion,
        customTaxRate,
        customTaxType
      );

      setResults({
        exists: true,
        decimalOdds,
        probabilities: probabilities.map(p => p * 100),
        totalProbability: totalProb * 100,
        stakes,
        returns,
        profit,
        tax: taxResult.amount,
        taxNote: taxResult.note,
        netProfit: profit - taxResult.amount
      });
      
    } catch (error) {
      console.error('Calculation error:', error);
    }
  };

  // JSX structure as previously defined...
  return (
    <div className="min-h-screen p-4 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto">
        {/* Component JSX structure */}
      </div>
    </div>
  );
};

export default ArbitrageCalculator;